import { forwardRef, ButtonHTMLAttributes } from 'react';
import { m } from 'framer-motion';
import { clsx } from 'clsx';

export type TagChipSize = 'sm' | 'md' | 'lg';

export interface TagChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  label: string;
  count?: number;
  selected?: boolean;
  size?: TagChipSize;
  removable?: boolean;
  onRemove?: () => void;
}

const sizeStyles: Record<TagChipSize, string> = {
  sm: 'px-2 py-0.5 text-xs gap-1',
  md: 'px-3 py-1 text-sm gap-1.5',
  lg: 'px-4 py-1.5 text-base gap-2',
};

export const TagChip = forwardRef<HTMLButtonElement, TagChipProps>(
  (
    {
      label,
      count,
      selected = false,
      size = 'md',
      removable = false,
      onRemove,
      disabled,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleRemoveClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove?.();
    };

    return (
      <m.button
        ref={ref}
        type="button"
        whileHover={{ scale: disabled ? 1 : 1.05 }}
        whileTap={{
          scale: disabled ? 1 : 0.9,
          transition: { type: 'spring', stiffness: 400, damping: 10 },
        }}
        transition={{ duration: 0.15 }}
        disabled={disabled}
        onClick={onClick}
        className={clsx(
          'inline-flex items-center justify-center rounded-full font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-accent/50',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          selected
            ? 'bg-accent text-white'
            : 'bg-text/10 text-text hover:bg-text/20',
          sizeStyles[size],
          className
        )}
        {...props}
      >
        <span>{label}</span>
        {count !== undefined && (
          <span
            className={clsx(
              'font-normal',
              selected ? 'text-white/70' : 'text-text/50'
            )}
          >
            {count}
          </span>
        )}
        {removable && (
          <button
            type="button"
            onClick={handleRemoveClick}
            className={clsx(
              'ml-0.5 rounded-full p-0.5 transition-colors',
              'hover:bg-white/20 focus:outline-none',
              selected ? 'text-white/70 hover:text-white' : 'text-text/50 hover:text-text'
            )}
            aria-label={`${label} 태그 제거`}
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </m.button>
    );
  }
);

TagChip.displayName = 'TagChip';
